import { useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import AppLayout from "layouts/app-layout"
import Transition from "components/transition"
import Create from "pages/branch/create"
import moment from "moment/moment"
import Edit from "pages/branch/edit"
import Delete from "pages/branch/delete"
import Filter from "pages/branch/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import useBranches from "repositories/branch"
import { Pagination } from "components"
import { lang } from "config"

export default function List(props) {
    // Logged in user ability
    const { can } = useAuth()

    // React router navigate hook
    const navigate = useNavigate()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const name = searchParams.get('name')
    const contact = searchParams.get('contact')
    const address = searchParams.get('address')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const searchInput = useRef()

    // Branch data
    const { data: branches, isLoading, error, mutate } = useBranches({
        page,
        search,
        name,
        contact,
        address,
        from,
        to
    })

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on branch data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoading) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [branches, isLoading])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Form success handler
    const handleSuccess = () => {
        mutate()
        nProgress.done()
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchParams({ search: e.target.value })} value={searchParams.get('search')} type="text" placeholder={`${lang.search} ${lang.branch}`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, contact, address, from, to }).filter(([_, v]) => v != null))} />
                </div>
                {can('branch.create') && (
                    <div>
                        <Create success={handleSuccess} />
                    </div>
                )}
            </div>
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.image}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.branch}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.code}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.contact}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.address}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.created_at}</th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">Action</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                        {/* When loading */}
                        {isLoading && (
                            <tr className="text-center">
                                <td colSpan="8" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.loading_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available */}
                        {branches?.data.length === 0 && !search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="8" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available on searching */}
                        {branches?.data.length === 0 && search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="8" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_result}
                                </td>
                            </tr>
                        )}

                        {branches?.data.length > 0 && branches.data.map(row => (
                            <tr>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.image_url ? (
                                        <img
                                        src={row.image_url}
                                        alt={row.name || "branch"}
                                        className="h-10 w-10 rounded-lg object-cover border border-neutral-200"
                                        onError={(e) => {
                                            // prevent broken image icon loop
                                            e.currentTarget.style.display = "none"
                                        }}
                                        />
                                    ) : (
                                        <span className="text-neutral-400">—</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.name}
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {row.code}
                                </td>
                                <td className="px-6 py-4 text-xs whitespace-nowrap">
                                    {row.is_live ? (
                                        <span className="inline-flex px-2 py-1 font-medium text-green-700 rounded-full bg-green-50">Live</span>
                                    ) : (
                                        <span className="inline-flex px-2 py-1 font-medium rounded-full text-neutral-500 bg-neutral-100">Belum live</span>
                                    )}
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {row.contact}
                                </td>
                                <td className="px-6 py-4 text-xs text-neutral-500 w-40 align-top">
                                    <div className="line-clamp-3 break-words whitespace-normal">
                                        {row.address}
                                    </div>
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {moment(row.created_at).format('MMMM D, YYYY')}
                                </td>
                                <td class="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                    <div class="inline-flex items-center space-x-2">
                                        {can('branch.update') && (
                                            <Edit data={row} onSuccess={handleSuccess} />
                                        )}

                                        {can('branch.delete') && (
                                            <Delete data={row} onSuccess={handleSuccess} />
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination links={branches?.links} from={branches?.from} to={branches?.to} total={branches?.total} />
        </div>
    )
}